/**
 * Singleton managing the Twilio Voice Device lifecycle.
 *
 * Plain JS class with an event-emitter pattern so it works with Vue 3 Options
 * API components (no reactive refs — components subscribe via on/off).
 *
 * Lifecycle: initialize() creates the Device but does NOT register it.
 * Call register()/unregister() to control availability for inbound calls.
 *
 * Cross-tab sync: status changes (available/offline/busy) are broadcast via
 * BroadcastChannel so all open tabs show the same state.
 *
 * Usage:
 *   import { twilioDevice } from '@/services/TwilioDeviceService'
 *   await twilioDevice.initialize()       // after auth
 *   await twilioDevice.register()         // user goes "available"
 *   twilioDevice.on('incoming', handler)  // in component mounted()
 */
import { z } from 'zod'
import { getVoiceToken, getUnreadVoicemailCount } from '@/services/api/voiceApi'
import { BrowserStore } from '@/utils/BrowserStore'

const STATUS_CHANNEL = 'twilio-phone-status'
const VOICEMAIL_POLL_INTERVAL = 120000

const voicemailStore = new BrowserStore(
  'unreadVoicemailCache',
  z.object({ count: z.number().default(0), ts: z.number().default(0) }),
)

const phoneAvailabilityStore = new BrowserStore(
  'twilioPhoneAvailability',
  z.object({ available: z.boolean().default(false) }),
)

const phoneStatusStore = new BrowserStore(
  'twilioPhoneStatus',
  z.object({ status: z.string().default('offline') }),
)

class TwilioDeviceService {
  constructor() {
    /** @type {Device | null} */
    this.device = null
    /** @type {import('@twilio/voice-sdk').Call | null} */
    this.activeCall = null
    this.currentCallContext = null
    /** @type {string | null} */
    this.identity = null
    this._listeners = {}
    this._tokenRefreshTimer = null
    this._initialized = false
    this._registered = false
    this._isDestroying = false

    /** @type {'offline' | 'available' | 'busy'} */
    this._status = 'offline'
    this._unreadVoicemails = voicemailStore.load().count
    this._voicemailPollTimer = null
    this._channel = new BroadcastChannel(STATUS_CHANNEL)
    this._channel.onmessage = (e) => this._onCrossTabMessage(e.data)
    this._onStorageEvent = (e) => {
      if (e.key === 'twilioPhoneStatus' && e.newValue) {
        try {
          const { status } = JSON.parse(e.newValue)
          if (status && status !== this._status && !this.activeCall) {
            this._status = status
            this._emit('statusChanged', status)
          }
        } catch {
          // ignore
        }
      }
      if (e.key !== 'unreadVoicemailCache' || !e.newValue) return
      try {
        const { count } = JSON.parse(e.newValue)
        if (typeof count === 'number' && count !== this._unreadVoicemails) {
          this._unreadVoicemails = count
          this._emit('unreadVoicemailsChanged', count)
        }
      } catch {
        // ignore
      }
    }
    window.addEventListener('storage', this._onStorageEvent)
  }

  // ─── Event emitter ──────────────────────────────────────────────────────

  /**
   * @param {string} event
   * @param {Function} fn
   */
  on(event, fn) {
    if (!this._listeners[event]) this._listeners[event] = []
    this._listeners[event].push(fn)
  }

  /**
   * @param {string} event
   * @param {Function} fn
   */
  off(event, fn) {
    const list = this._listeners[event]
    if (!list) return
    this._listeners[event] = list.filter((f) => f !== fn)
  }

  /** @param {string} event @param {...any} args */
  _emit(event, ...args) {
    const list = this._listeners[event]
    if (!list) return
    for (const fn of list) {
      try {
        fn(...args)
      } catch (e) {
        console.error(`[TwilioDevice] listener error on "${event}":`, e)
      }
    }
  }

  // ─── Cross-tab status sync ──────────────────────────────────────────────

  /** @returns {'offline' | 'available' | 'busy'} */
  get status() {
    return this._status
  }

  _setStatus(status, { sync = !this._isDestroying } = {}) {
    if (this._status === status) return
    // Never downgrade from busy while a call is active
    if (this._status === 'busy' && status !== 'busy' && this.activeCall) return
    this._status = status
    if (sync) {
      phoneStatusStore.save({ status })
      this._channel.postMessage({ type: 'status', status })
    }
    this._emit('statusChanged', status)
  }

  // ─── Unread voicemail count ──────────────────────────────────────────

  get unreadVoicemails() {
    return this._unreadVoicemails
  }

  _setUnreadVoicemails(count) {
    if (this._unreadVoicemails === count) return
    this._unreadVoicemails = count
    voicemailStore.save({ count, ts: Date.now() })
    this._emit('unreadVoicemailsChanged', count)
  }

  async fetchUnreadVoicemails() {
    try {
      const count = await getUnreadVoicemailCount()
      this._setUnreadVoicemails(count)
    } catch {
      // silently ignore — polling will retry
    }
  }

  startVoicemailPolling() {
    this.stopVoicemailPolling()
    const cached = voicemailStore.load()
    if (Date.now() - cached.ts < VOICEMAIL_POLL_INTERVAL) {
      this._unreadVoicemails = cached.count
      this._emit('unreadVoicemailsChanged', cached.count)
    } else {
      this.fetchUnreadVoicemails()
    }
    this._voicemailPollTimer = setInterval(
      () => this.fetchUnreadVoicemails(),
      VOICEMAIL_POLL_INTERVAL,
    )
  }

  stopVoicemailPolling() {
    if (this._voicemailPollTimer) {
      clearInterval(this._voicemailPollTimer)
      this._voicemailPollTimer = null
    }
  }

  _broadcastCallContext() {
    if (this.activeCall && this.currentCallContext) {
      const context = {
        callerNumber: this.activeCall.parameters?.From
          || this.activeCall.customParameters?.get('to_phone_number')
          || '',
        workId: this.currentCallContext.workId,
        entityName: this.currentCallContext.entityName,
      }
      try { localStorage.setItem('twilioActiveCallContext', JSON.stringify(context)) } catch {}
      this._channel.postMessage({ type: 'callContext', context })
    } else {
      try { localStorage.removeItem('twilioActiveCallContext') } catch {}
      this._channel.postMessage({ type: 'callContext', context: null })
    }
  }

  requestFocusCallTab() {
    this._channel.postMessage({ type: 'command', action: 'focus' })
  }

  _onCrossTabMessage(data) {
    if (data.type === 'status' && data.status !== this._status) {
      // Don't let another tab override busy while we have an active call
      if (this._status === 'busy' && data.status !== 'busy' && this.activeCall) return
      this._status = data.status
      this._emit('statusChanged', data.status)
    }
    if (data.type === 'callContext') {
      if (!this.activeCall) {
        this._emit('crossTabCall', data.context)
      }
    }
    if (data.type === 'command') {
      if (data.action === 'focus' && this.activeCall) {
        window.focus()
      }
      if (data.action === 'register' && this._initialized && !this._registered) {
        this.register()
      }
      if (data.action === 'unregister' && this._registered) {
        this.unregister()
      }
      if (data.action === 'rebroadcastContext' && this.activeCall) {
        this._broadcastCallContext()
      }
    }
  }

  // ─── Lifecycle ──────────────────────────────────────────────────────────

  async initialize() {
    if (this._initialized) return
    this._isDestroying = false
    try {
      const { token, identity } = await getVoiceToken()
      this.identity = identity

      const { Device } = await import('@twilio/voice-sdk')
      this.device = new Device(token, {
        edge: 'ashburn',
        closeProtection: true,
        logLevel: 'warn',
        codecPreferences: ['opus', 'pcmu'],
      })
      this._bindDeviceEvents()
      this._initialized = true
      this.startVoicemailPolling()

      const stored = phoneAvailabilityStore.load()
      if (stored.available) {
        this.register()
      }
    } catch (e) {
      console.error('[TwilioDevice] initialization failed:', e)
      this._emit('deviceError', e)
    }
  }

  async register() {
    if (!this.device) {
      this._channel.postMessage({ type: 'command', action: 'register' })
      return
    }
    if (this._registered) return
    try {
      await this.device.register()
      this._registered = true
      const shared = phoneStatusStore.load().status
      this._setStatus(shared === 'busy' ? 'busy' : 'available')
      phoneAvailabilityStore.save({ available: true })
      this._emit('deviceRegistered', { identity: this.identity })
    } catch (e) {
      console.error('[TwilioDevice] registration failed:', e)
      console.error('[TwilioDevice] registration error details:', {
        name: e.name,
        message: e.message,
        code: e.code,
        causes: e.causes,
      })
      this._emit('deviceError', e)
    }
  }

  async unregister() {
    if (!this.device || !this._registered) {
      this._channel.postMessage({ type: 'command', action: 'unregister' })
      return
    }
    try {
      this._registered = false
      this._setStatus('offline')
      phoneAvailabilityStore.save({ available: false })
      this.device.unregister()
      this._emit('deviceUnregistered')
    } catch (e) {
      console.error('[TwilioDevice] unregistration failed:', e)
    }
  }

  _bindDeviceEvents() {
    if (!this.device) return

    this.device.on('incoming', (call) => {
      if (this.activeCall) return
      this.activeCall = call
      this._bindCallEvents(call)

      const callSid = call.parameters?.CallSid || null
      const matchCount = parseInt(
        call.customParameters?.get('match_count') || '0',
        10,
      )
      let matchedEntities = []
      try {
        matchedEntities = JSON.parse(
          call.customParameters?.get('matched_entities_json') || '[]',
        )
      } catch {
        // ignore parse errors
      }

      this.currentCallContext = {
        callSid,
        workId: null,
        entityId:
          matchCount === 1 ? matchedEntities[0]?.entity_id : null,
        entityName:
          matchCount === 1 ? matchedEntities[0]?.entity_name : null,
        matchCount,
        matchedEntities,
        status: 'pending',
      }

      this._setStatus('busy')

      this._emit('incoming', {
        call,
        callSid,
        matchCount,
        matchedEntities,
        fromNumber: call.parameters?.From,
      })
    })

    this.device.on('tokenWillExpire', async () => {
      try {
        const { token } = await getVoiceToken()
        this.device?.updateToken(token)
      } catch (e) {
        console.error('[TwilioDevice] token refresh failed:', e)
        this._emit('deviceError', e)
      }
    })

    this.device.on('error', (err) => {
      console.error('[TwilioDevice] device error:', err)
      console.error('[TwilioDevice] device error details:', {
        name: err.name,
        message: err.message,
        code: err.code,
        causes: err.causes,
      })
      this._emit('deviceError', err)
    })

    this.device.on('unregistered', () => {
      this._registered = false
      // A device can unregister because its tab is closing or it loses its
      // connection. That only affects this tab; only unregister() represents
      // the user's shared "go unavailable" action.
      this._setStatus('offline', { sync: false })
      this._emit('deviceUnregistered')
    })
  }

  /** @param {import('@twilio/voice-sdk').Call} call */
  _bindCallEvents(call) {
    const guardUnload = () => {
      this._beforeUnloadHandler = (e) => { e.preventDefault() }
      window.addEventListener('beforeunload', this._beforeUnloadHandler)
    }
    const unguardUnload = () => {
      if (this._beforeUnloadHandler) {
        window.removeEventListener('beforeunload', this._beforeUnloadHandler)
        this._beforeUnloadHandler = null
      }
    }
    const cleanupCall = () => {
      this.activeCall = null
      this.currentCallContext = null
      this._setStatus(this._registered ? 'available' : 'offline')
      this._broadcastCallContext()
      unguardUnload()
    }

    call.on('accept', () => {
      if (this.currentCallContext) {
        this.currentCallContext.connectedAt = Date.now()
      }
      this._setStatus('busy')
      guardUnload()
      this._broadcastCallContext()
      this._emit('callConnected', call)
    })

    call.on('disconnect', () => {
      cleanupCall()
      this._emit('callDisconnected')
    })

    call.on('cancel', () => {
      let crossTabContext = null
      try {
        const stored = localStorage.getItem('twilioActiveCallContext')
        if (stored) crossTabContext = JSON.parse(stored)
      } catch {}

      this.activeCall = null
      this.currentCallContext = null
      this._broadcastCallContext()
      unguardUnload()
      this._setStatus(this._registered ? 'available' : 'offline')
      this._emit('callDisconnected')

      if (crossTabContext) {
        this._emit('crossTabCall', crossTabContext)
      }
      this._channel.postMessage({ type: 'command', action: 'rebroadcastContext' })
    })

    call.on('error', (err) => {
      console.error('[TwilioDevice] call error:', err)
      cleanupCall()
      this._emit('callError', err)
    })

    call.on('reject', () => {
      cleanupCall()
      this._emit('callDisconnected')
    })

    call.on('ringing', () => {
      this._emit('callRinging', call)
    })
  }

  // ─── Outbound ───────────────────────────────────────────────────────────

  /**
   * Place an outbound call via the Twilio Device.
   * @param {{ toPhoneNumber: string, fromPhoneNumber: string, workId: string | number }} params
   * @returns {Promise<import('@twilio/voice-sdk').Call>}
   */
  async connect({ toPhoneNumber, fromPhoneNumber, workId, entityId, entityName }) {
    if (!this.device) {
      throw new Error('Twilio Device not initialized')
    }
    if (this.activeCall) {
      throw new Error('A call is already active')
    }

    if (!this._registered) {
      await this.register()
    }

    const call = await this.device.connect({
      params: {
        to_phone_number: toPhoneNumber,
        from_phone_number: fromPhoneNumber,
        work_id: String(workId),
      },
    })

    this.activeCall = call
    this._bindCallEvents(call)

    this.currentCallContext = {
      callSid: null,
      workId: workId || null,
      entityId: entityId || null,
      entityName: entityName || null,
      matchCount: -1,
      matchedEntities: [],
      status: workId ? 'linked' : 'pending',
    }

    this._setStatus('busy')
    this._emit('callConnecting', call)
    return call
  }

  // ─── Inbound ────────────────────────────────────────────────────────────

  /** @param {import('@twilio/voice-sdk').Call} call */
  acceptIncoming(call) {
    call.accept()
  }

  /** @param {import('@twilio/voice-sdk').Call} call */
  rejectIncoming(call) {
    call.reject()
    if (this.activeCall === call) {
      this.activeCall = null
    }
  }

  // ─── Engagement linking ──────────────────────────────────────────────────

  linkEngagement({ workId, entityId, entityName }) {
    if (this.currentCallContext) {
      this.currentCallContext.workId = workId
      this.currentCallContext.entityId = entityId
      this.currentCallContext.entityName = entityName
      this.currentCallContext.status = 'linked'
      this._broadcastCallContext()
      this._emit('engagementLinked', this.currentCallContext)
    }
  }

  // ─── Call controls ──────────────────────────────────────────────────────

  toggleMute() {
    if (!this.activeCall) return false
    const muted = !this.activeCall.isMuted()
    this.activeCall.mute(muted)
    this._emit('muteChanged', muted)
    return muted
  }

  disconnect() {
    if (this.activeCall) {
      this.activeCall.disconnect()
      this.activeCall = null
      this.currentCallContext = null
    }
  }

  // ─── Media streams (for TranscriptionEngine) ────────────────────────────

  getLocalStream() {
    return this.activeCall?.getLocalStream() ?? null
  }

  getRemoteStream() {
    return this.activeCall?.getRemoteStream() ?? null
  }

  // ─── Teardown ───────────────────────────────────────────────────────────

  async destroy() {
    // destroy() tears down this tab (for example, during logout). It must not
    // make another open tab unavailable through the shared status channel.
    this._isDestroying = true
    this.disconnect()
    this.stopVoicemailPolling()
    window.removeEventListener('storage', this._onStorageEvent)
    if (this.device) {
      this.device.unregister()
      this.device.destroy()
      this.device = null
    }
    this._initialized = false
    this._registered = false
    this._setStatus('offline')
    this._unreadVoicemails = 0
    this.identity = null
    this.currentCallContext = null
    this._listeners = {}
  }

  get isInitialized() {
    return this._initialized
  }

  get isRegistered() {
    return this._registered
  }

  get isBusy() {
    return this.activeCall !== null
  }
}

export const twilioDevice = new TwilioDeviceService()
